import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckSplit, GetSplitStatus, StartRevenueSplit } from '../../_services/importRevenue/getSplitStatus.service';
import { readApiError } from './useImportStatus';

// The split is queued on a celery worker, so PUT only means "accepted". The real
// state lives on GET /releases/release-collections/split-status/{import_id}/.
export const SPLIT_POLL_INTERVAL = 3000;                  // ~10k rows takes about 45s
export const SPLIT_MAX_POLL_DURATION = 15 * 60 * 1000;    // stop nagging the API after 15 min
export const SPLIT_PENDING_WARNING = 60 * 1000;           // nothing is consuming the queue

export const isSplitTerminal = (status) => status === 'success' || status === 'failed';

// The upload id is not on the import-log row. That row's import_id is the
// ImportExcelLog id (245); the split endpoints want the upload id the upload response
// and /releases/import-status/<id>/ return (288), and they answer
// 400 {error, expected_import_id} naming it rather than guessing and splitting the
// wrong month. Cache the pairing so only the very first call is ever made with the
// log id -- every call after it, including on later page loads, sends the upload id.
const SPLIT_ID_MAP_KEY = 'splitUploadIds';

const readIdMap = () => {
  try {
    const raw = localStorage.getItem(SPLIT_ID_MAP_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const rememberUploadId = (logId, uploadId) => {
  if (!logId || !uploadId) return;
  try {
    localStorage.setItem(SPLIT_ID_MAP_KEY, JSON.stringify({ ...readIdMap(), [logId]: uploadId }));
  } catch (e) {
    // a full or disabled store only costs us the one extra 400 next time
  }
};

const resolveUploadId = (logId) => (logId ? readIdMap()[logId] || logId : '');

const readExpectedImportId = (response) => {
  const data = (response && response.data) || {};
  const expected = data.expected_import_id;
  return expected || expected === 0 ? String(expected) : '';
};

export default function useSplitStatus({ importId, limit, offset, onSuccess, onFailed, onError } = {}) {
  // the id every split endpoint is called with: the upload id once it is known
  const logId = importId || importId === 0 ? String(importId) : '';
  const [splitImportId, setSplitImportId] = useState(() => resolveUploadId(logId));
  const [status, setStatus] = useState(null);
  const [splitStatus, setSplitStatus] = useState(null);
  const [canSplit, setCanSplit] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [pollMessage, setPollMessage] = useState('');
  const [pollError, setPollError] = useState('');

  const idRef = useRef(resolveUploadId(logId));
  const logIdRef = useRef(logId);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const startedAtRef = useRef(0);

  // keep the callbacks fresh without restarting the poll loop
  const handlersRef = useRef({});
  handlersRef.current = { onSuccess, onFailed, onError };

  // check-split paginates the rows it inspects and returns nothing without these
  const pageRef = useRef({});
  pageRef.current = { limit, offset };

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    setIsPolling(false);
  }, [clearTimer]);

  // a request that failed for good: stop the loop, surface it, never retry
  const failPolling = useCallback((message, httpStatus) => {
    stopPolling();
    setInProgress(false);
    setPollError(message);
    if (handlersRef.current.onError) handlersRef.current.onError({ status: httpStatus, message });
  }, [stopPolling]);

  // the 400 is the only place the upload id is published, so read it, keep it, retry
  const callWithResolvedId = useCallback(async (fn) => {
    let response = await fn(idRef.current);
    const expected = response && response.status === 400 ? readExpectedImportId(response) : '';
    if (expected && expected !== String(idRef.current)) {
      rememberUploadId(logIdRef.current, expected);
      idRef.current = expected;
      setSplitImportId(expected);
      response = await fn(expected);
    }
    return response;
  }, []);

  const poll = useCallback(async (id) => {
    const response = await GetSplitStatus(id);
    if (cancelledRef.current) return;

    // split-status does not exist on older backends -- stop rather than hammer it
    if (!response || response.status !== 200) {
      const httpStatus = response && response.status;
      failPolling(
        readApiError(response) || 'Could not read split progress. Please reload the page.',
        httpStatus,
      );
      return;
    }

    const data = response.data || {};
    setStatus(data);
    setSplitStatus(data.split_status);

    if (data.split_status === 'success') {
      stopPolling();
      setInProgress(false);
      setCanSplit(false);
      setPollMessage('');
      // the outcome / skipped breakdown is only attached once the split finishes
      if (handlersRef.current.onSuccess) handlersRef.current.onSuccess(data);
      return;
    }

    if (data.split_status === 'failed') {
      stopPolling();
      setInProgress(false);
      // an empty error_log means an operator reset the status to make the split
      // re-runnable, not that anything crashed -- offer a retry, not an error
      const retryable = !data.error_log;
      setCanSplit(true);
      setPollMessage('');
      if (handlersRef.current.onFailed) handlersRef.current.onFailed({ ...data, retryable });
      return;
    }

    const elapsed = Date.now() - startedAtRef.current;

    if (elapsed > SPLIT_MAX_POLL_DURATION) {
      stopPolling();
      setPollMessage('Still splitting. You can leave this page and check back later.');
      return;
    }

    // stuck on pending means no worker is consuming the queue: infra, not the user
    if (data.split_status === 'pending' && elapsed > SPLIT_PENDING_WARNING) {
      setPollMessage('Queued for over a minute — no worker has picked it up yet.');
    } else if (data.split_status === 'processing') {
      // a worker took it after all, drop the warning
      setPollMessage('');
    }

    timerRef.current = setTimeout(() => poll(id), SPLIT_POLL_INTERVAL);
  }, [stopPolling, failPolling]);

  const beginPolling = useCallback((initialStatus) => {
    clearTimer();
    cancelledRef.current = false;
    startedAtRef.current = Date.now();
    setStatus((prev) => prev || { split_status: initialStatus || 'pending', progress_percent: 0 });
    setSplitStatus(initialStatus || 'pending');
    setInProgress(true);
    setCanSplit(false);
    setPollMessage('');
    setPollError('');
    setIsPolling(true);
    poll(idRef.current);
  }, [clearTimer, poll]);

  // the split is finished and there is nothing left to poll
  const markSplit = useCallback((data) => {
    stopPolling();
    setInProgress(false);
    setCanSplit(false);
    setSplitStatus('success');
    if (data) setStatus((prev) => ({ ...(prev || {}), ...data }));
  }, [stopPolling]);

  const check = useCallback(async () => {
    const response = await callWithResolvedId((id) => CheckSplit(id, pageRef.current));
    // a 404 only means this upload has no rows to split, leave the button disabled
    if (!response || response.status !== 200) {
      setIsChecked(true);
      return null;
    }

    const data = response.data || {};
    // the old backend answers {import_id, split} and nothing else. can_split is what
    // the button should read, so fall back to the split flag until it ships.
    const hasSplitState = typeof data.can_split === 'boolean';
    setIsChecked(true);
    setSplitStatus(data.split_status || (data.split ? 'success' : 'not_started'));
    setInProgress(!!data.in_progress);
    setCanSplit(hasSplitState ? data.can_split : !data.split);

    // a worker is already on this import, join it instead of showing a dead button
    if (hasSplitState && data.in_progress) beginPolling(data.split_status);
    return data;
  }, [callWithResolvedId, beginPolling]);

  const start = useCallback(async () => {
    setPollError('');
    setPollMessage('');
    setIsStarting(true);
    const response = await callWithResolvedId(StartRevenueSplit);
    setIsStarting(false);

    const data = (response && response.data) || {};

    if (response && response.status === 202) {
      setStatus(null);
      beginPolling(data.split_status || 'pending');
      return { started: true };
    }

    // 409 is information, not an error: a worker is on it, or it is already done
    if (response && response.status === 409) {
      if (data.split_status === 'success') {
        markSplit(data);
        return { started: false, alreadyDone: true };
      }
      setStatus(null);
      beginPolling(data.split_status || 'processing');
      return { started: true, alreadyRunning: true };
    }

    // the old backend split inside the request, so a 200 means it is already finished
    if (response && response.status === 200) {
      markSplit(null);
      return { started: false, alreadyDone: true, legacy: true, message: data.message };
    }

    const message = readApiError(response) || 'Could not start the revenue split.';
    setPollError(message);
    if (handlersRef.current.onError) {
      handlersRef.current.onError({ status: response && response.status, message });
    }
    return { started: false, error: message };
  }, [callWithResolvedId, beginPolling, markSplit]);

  const dismiss = useCallback(() => {
    setPollMessage('');
    setPollError('');
  }, []);

  // check on mount, and stop the loop when the page is left
  useEffect(() => {
    logIdRef.current = logId;
    idRef.current = resolveUploadId(logId);
    setSplitImportId(idRef.current);
    if (idRef.current) check();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  return {
    splitImportId,
    status,
    splitStatus,
    canSplit,
    inProgress,
    isStarting,
    isPolling,
    isChecked,
    pollMessage,
    pollError,
    // there is something worth showing: the request is in flight, a run is going, or
    // it finished and has not been dismissed
    isActive: isStarting || isPolling || isSplitTerminal(splitStatus) || !!pollMessage || !!pollError,
    start,
    check,
    dismiss,
    stopPolling,
  };
}
